/* ============================================================
   Safe-area & viewport helpers for the Intraop PWA.
   ------------------------------------------------------------
   Used everywhere we need to push UI below the Dynamic Island /
   notch (top bars, headers, fixed overlays) or above the home
   indicator (bottom bars).

   Why these constants instead of the .safe-top / .safe-bottom
   CSS classes?

   The CSS classes work as long as inline styles don't set the
   `padding` shorthand (which overrides `padding-top`). Every
   measurement page in this app DID set inline `padding: '12px
   16px'` — silently overriding the safe-area padding-top and
   pushing the angle HUD behind the notch in standalone-PWA mode.

   By keeping the safe-area math inline, we eliminate the
   specificity conflict completely. Components consume these
   constants where they need them; no surprise overrides. */

/** Raw env() value for the top safe-area inset (status bar /
 *  Dynamic Island / notch). Returns 0 when there's no inset. */
export const SAFE_INSET_TOP = "env(safe-area-inset-top, 0px)";

/** Raw env() value for the bottom safe-area inset (home indicator). */
export const SAFE_INSET_BOTTOM = "env(safe-area-inset-bottom, 0px)";

/** Padding-top value that respects the safe-area inset and never
 *  drops below `minPx`. Use for any top bar that sits in a
 *  position:absolute or position:fixed layout. */
export function safeTopPad(minPx: number = 12): string {
  return `max(${SAFE_INSET_TOP}, ${minPx}px)`;
}

/** Padding-bottom value with a minimum, for bottom bars. */
export function safeBottomPad(minPx: number = 12): string {
  return `max(${SAFE_INSET_BOTTOM}, ${minPx}px)`;
}

/** Top offset for a row that sits BELOW a top bar — adds the safe-area
 *  inset (so it clears the notch) plus the visible top-bar content
 *  height plus a small gap below it.
 *
 *  Pass the height of the visible top-bar content (button + paddings,
 *  excluding the safe-area portion) to get a layout-correct offset. */
export function belowTopBar(visibleContentPx: number = 64, gapPx: number = 12): string {
  return `calc(${SAFE_INSET_TOP} + ${visibleContentPx + gapPx}px)`;
}
